/* ===== Joy Assistant — draft persistence (Firestore + Storage) ===== */
import { db } from '../config/firebase.js?v=83.21';
import { getCurrentUser } from './auth.js?v=83.21';
import { uploadFile } from './storage.js?v=83.21';
import { logger } from '../utils/logger.js?v=83.21';
import { getCurrentSettings, setCurrentSettings } from './settings.js?v=83.21';
import { saveCampaignSettingsBackend } from './backend.js?v=83.21';

const DRAFT_ID = 'draft';
const MAX_IMAGES = 5;
const SAVE_DEBOUNCE_MS = 900;
/* Flux v2 — 6 pași: Poze → Brand → Utile → Meniu/Recenzie → Oferte → Creștere */
const TOTAL_STEPS = 6;
const FLOW_VERSION = 2;

let saveTimer = null;
let pendingSave = null;

export const SLIDESHOW_SECONDS_OPTIONS = [3, 5, 10, 15];
export const DEFAULT_SLIDESHOW_SECONDS = 5;

export const JOY_STEP2_MODULES = [
  { id: 'wifi', label: 'WiFi', preview: 'WiFi', icon: '📶', dock: 'utils' },
  { id: 'location', label: 'Locație', preview: 'Locație', icon: '📍', dock: 'utils' },
  { id: 'hours', label: 'Program', preview: 'Program', icon: '🕐', dock: 'utils' },
  { id: 'reservation', label: 'Comenzi / Rezervări', preview: 'Comenzi', icon: '📞', dock: 'utils' }
];

export const JOY_STEP3_MODULES = [
  { id: 'logo', label: 'Logo', preview: 'Logo', icon: '🏷️', dock: 'main' },
  { id: 'instagram', label: 'Instagram', preview: 'Instagram', icon: '📷', dock: 'main' },
  { id: 'facebook', label: 'Facebook', preview: 'Facebook', icon: 'f', dock: 'main' },
  { id: 'tiktok', label: 'TikTok', preview: 'TikTok', icon: '♪', dock: 'main' },
  { id: 'googleReview', label: 'Recenzie Google', preview: 'Recenzie', icon: 'G', dock: 'main' },
  { id: 'menu', label: 'Meniu / Listă prețuri', preview: 'Meniu', icon: '📋', dock: 'main' }
];

/* Grupare v2: pasul 2 = identitate brand, pasul 4 = meniu & recenzie */
export const JOY_BRAND_MODULE_IDS = ['logo', 'instagram', 'facebook', 'tiktok'];
export const JOY_DUO_MODULE_IDS = ['menu', 'googleReview'];
export const JOY_STEP_BRAND_MODULES = JOY_STEP3_MODULES.filter((m) =>
  JOY_BRAND_MODULE_IDS.includes(m.id)
);
export const JOY_STEP_DUO_MODULES = JOY_DUO_MODULE_IDS.map((id) =>
  JOY_STEP3_MODULES.find((m) => m.id === id)
).filter(Boolean);

export const JOY_GROWTH_OPTIONS = [
  { id: 'campaign', label: 'Campanie', icon: '🎯' },
  { id: 'loyalty', label: 'Fidelizare', icon: '⭐' },
  { id: 'events', label: 'Evenimente', icon: '📅' },
  { id: 'none', label: 'Nu acum', icon: '⏭️' }
];

export const JOY_DOCK_UTILS = ['wifi', 'hours', 'location', 'reservation'];
export const JOY_DOCK_MAIN = ['logo', 'menu', 'googleReview', 'instagram', 'facebook', 'tiktok'];

/** Joy module id → admin subsection (formular configurare) */
export const JOY_MODULE_SUBSECTION = {
  wifi: 'wifi-info',
  hours: 'location-hours',
  location: 'location-map',
  menu: 'location-menu',
  facebook: 'social-media',
  instagram: 'social-media',
  tiktok: 'social-media',
  reservation: 'reservation',
  googleReview: 'feedback',
  offers: 'client-promo-gallery',
  logo: 'location-logo'
};

/** Elemente client reale care deschid configurarea modulului */
export const JOY_PREVIEW_CLICK_SELECTORS = {
  wifi: '#clientWifiFab',
  hours: '#clientLocationHoursFab, #clientMenuLocationHoursBtn',
  location: '#clientLocationMapFab, #clientMenuLocationMapBtn',
  reservation: '#clientReservationFab, #clientMenuReservationBtn',
  menu: '#locationMenuBtn',
  googleReview: '#feedbackBtn',
  facebook: '#socialBtnFacebook',
  instagram: '#socialBtnInstagram',
  tiktok: '#socialBtnTiktok',
  logo: '#clientPublicBrandLogo, #clientTemplateBrandLogo, .client-template-header__logo',
  offers: '#clientPromoGalleryTrigger'
};

const STEP2_IDS = JOY_STEP2_MODULES.map((m) => m.id);
const STEP3_IDS = JOY_STEP3_MODULES.map((m) => m.id);
const GROWTH_IDS = JOY_GROWTH_OPTIONS.map((m) => m.id);

function draftRef(uid) {
  return db.collection('merchants').doc(uid).collection('joyAssistant').doc(DRAFT_ID);
}

function sanitizeSlideshowSeconds(raw) {
  const n = Number(raw);
  return SLIDESHOW_SECONDS_OPTIONS.includes(n) ? n : DEFAULT_SLIDESHOW_SECONDS;
}

function sanitizeEditableSection(raw = {}) {
  return {
    title: String(raw.title || '').trim().slice(0, 120),
    text: String(raw.text || '').trim().slice(0, 2000),
    imageUrl: String(raw.imageUrl || '').trim()
  };
}

function sanitizeAiBlob(raw) {
  if (!raw || typeof raw !== 'object') return null;
  try {
    const json = JSON.stringify(raw);
    return json.length <= 30000 ? JSON.parse(json) : null;
  } catch (_) {
    return null;
  }
}

export function sanitizeJoyDraft(raw = {}) {
  const images = Array.isArray(raw.images)
    ? raw.images.map((u) => String(u || '').trim()).filter(Boolean).slice(0, MAX_IMAGES)
    : [];

  const step2Modules = Array.isArray(raw.step2Modules)
    ? raw.step2Modules.filter((id) => STEP2_IDS.includes(id))
    : [];

  const step3Modules = Array.isArray(raw.step3Modules)
    ? raw.step3Modules.filter((id) => STEP3_IDS.includes(id))
    : [];

  const legacyCompleted = Array.isArray(raw.completedModules) ? raw.completedModules : [];
  const legacyEnabled = Array.isArray(raw.enabledModules) ? raw.enabledModules : [];
  const legacy = [...legacyCompleted, ...legacyEnabled];

  const mergedStep2 = step2Modules.length
    ? step2Modules
    : legacy.filter((id) => STEP2_IDS.includes(id));
  const mergedStep3 = step3Modules.length
    ? step3Modules
    : legacy.filter((id) => STEP3_IDS.includes(id));

  const isV2 = Number(raw.flowVersion) >= 2;
  let step = Number(raw.step);
  if (!Number.isFinite(step) || step < 1) {
    step = raw.phase === 'modules' ? 2 : 1;
  }
  if (!isV2) {
    // Migrare flux vechi (5 pași) → flux nou (6 pași):
    // vechi: 1 poze, 2 utile, 3 brand+duo, 4 oferte, 5 creștere
    // nou:   1 poze, 2 brand, 3 utile, 4 duo, 5 oferte, 6 creștere
    const map = { 1: 1, 2: 3, 3: 2, 4: 5, 5: 6 };
    step = map[Math.min(step, 5)] || 1;
  }
  if (step > TOTAL_STEPS) step = TOTAL_STEPS;

  const growthModule = GROWTH_IDS.includes(raw.growthModule) ? raw.growthModule : null;

  const sec = sanitizeEditableSection(raw.editableSection);
  const brandPicked = mergedStep3.filter((id) => JOY_BRAND_MODULE_IDS.includes(id));
  const duoPicked = mergedStep3.filter((id) => JOY_DUO_MODULE_IDS.includes(id));

  const phaseFor = (n, rawPhase, hasContent) =>
    step > n || (step === n && (rawPhase === 1 || hasContent)) ? 1 : 0;

  const step1Phase = raw.step1Phase === 1 || images.length > 0 ? 1 : 0;
  const step2Phase = phaseFor(2, isV2 ? raw.step2Phase : undefined, brandPicked.length > 0);
  const step3Phase = phaseFor(3, isV2 ? raw.step3Phase : undefined, mergedStep2.length > 0);
  const step4Phase = phaseFor(4, isV2 ? raw.step4Phase : undefined, duoPicked.length > 0);
  const step5Phase = phaseFor(5, isV2 ? raw.step5Phase : undefined, !!sec.title || !!sec.text);
  const step6Phase = phaseFor(6, isV2 ? raw.step6Phase : undefined, !!growthModule);

  return {
    step,
    step1Phase: step > 1 ? 1 : step1Phase,
    step2Phase,
    step3Phase,
    step4Phase,
    step5Phase,
    step6Phase,
    images,
    slideshowSeconds: sanitizeSlideshowSeconds(raw.slideshowSeconds),
    step2Modules: mergedStep2,
    step3Modules: mergedStep3,
    editableSection: sec,
    growthModule,
    ai: sanitizeAiBlob(raw.ai),
    status: raw.status === 'completed' ? 'completed' : 'draft'
  };
}

export async function loadJoyAssistantDraft(uid) {
  if (!uid) return sanitizeJoyDraft({});
  try {
    const snap = await draftRef(uid).get();
    if (!snap.exists) return sanitizeJoyDraft({});
    return sanitizeJoyDraft(snap.data());
  } catch (e) {
    logger.warn('[JoyAssistant] load draft failed:', e);
    return sanitizeJoyDraft({});
  }
}

export async function saveJoyAssistantDraft(uid, data) {
  if (!uid) return;
  const clean = sanitizeJoyDraft(data);
  const payload = {
    flowVersion: FLOW_VERSION,
    step: clean.step,
    step1Phase: clean.step1Phase,
    step2Phase: clean.step2Phase,
    step3Phase: clean.step3Phase,
    step4Phase: clean.step4Phase,
    step5Phase: clean.step5Phase,
    step6Phase: clean.step6Phase,
    images: clean.images,
    slideshowSeconds: clean.slideshowSeconds,
    step2Modules: clean.step2Modules,
    step3Modules: clean.step3Modules,
    editableSection: clean.editableSection,
    growthModule: clean.growthModule,
    ai: clean.ai,
    status: clean.status,
    updatedAt: firebase.firestore.FieldValue.serverTimestamp()
  };
  if (clean.status === 'completed') {
    payload.completedAt = firebase.firestore.FieldValue.serverTimestamp();
  }
  await draftRef(uid).set(payload, { merge: true });
}

export function scheduleJoyAssistantSave(uid, data) {
  if (!uid) return;
  pendingSave = sanitizeJoyDraft(data);
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(async () => {
    const toSave = pendingSave;
    pendingSave = null;
    if (!toSave) return;
    try {
      await saveJoyAssistantDraft(uid, toSave);
    } catch (e) {
      logger.warn('[JoyAssistant] save draft failed:', e);
    }
  }, SAVE_DEBOUNCE_MS);
}

export async function flushJoyAssistantSave(uid, data) {
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  const toSave = sanitizeJoyDraft(data || pendingSave || {});
  pendingSave = null;
  if (!uid) return;
  await saveJoyAssistantDraft(uid, toSave);
}

export async function uploadJoyAssistantImage(file) {
  const user = getCurrentUser();
  if (!user) throw new Error('Nu ești autentificat');
  return uploadFile(user.uid, 'joy-assistant', file);
}

function mergeJoySocialEnables(settings = {}, step3Modules = []) {
  const picked = new Set(step3Modules);
  const sm = { ...(settings.socialMedia || {}) };
  const fallbacks = {
    instagram: settings.clientInstagramUrl,
    facebook: settings.clientSocialUrl,
    tiktok: settings.clientTiktokUrl
  };
  let changed = false;
  for (const platform of ['facebook', 'instagram', 'tiktok']) {
    if (!picked.has(platform)) continue;
    const existing = sm[platform] || {};
    const link = String(existing.link || fallbacks[platform] || '').trim();
    sm[platform] = {
      enabled: true,
      link: link || existing.link || '',
      qrUrl: existing.qrUrl || ''
    };
    changed = true;
  }
  return changed ? sm : settings.socialMedia || {};
}

/** Aplică draft-ul Joy în setările principale ale comerciantului (la „Salvează și vizualizează”). */
export async function commitJoyAssistantDraftToSettings(uid, draft, options = {}) {
  if (!uid) throw new Error('Nu ești autentificat');

  const clean = sanitizeJoyDraft(draft);
  const current = getCurrentSettings() || {};
  const patch = {};

  const templateStyle = String(options.templateStyle || '').trim().toLowerCase();
  if (templateStyle) {
    patch.clientTemplateStyle = templateStyle;
  }

  const images = clean.images.filter((u) => typeof u === 'string' && u.startsWith('http'));
  if (images.length) {
    patch.bgURLs = images;
    patch.bgURL = images[0];
  }
  if (clean.slideshowSeconds) {
    patch.slideDuration = clean.slideshowSeconds;
  }

  const step2 = new Set(clean.step2Modules);
  if (step2.has('wifi')) patch.wifiEnabled = true;
  if (step2.has('location')) patch.locationMapEnabled = true;
  if (step2.has('hours')) patch.locationHoursEnabled = true;
  if (step2.has('reservation')) patch.reservationEnabled = true;

  const step3 = new Set(clean.step3Modules);
  if (step3.has('menu')) patch.menuEnabled = true;
  if (step3.has('googleReview')) patch.feedbackEnabled = true;

  const hasSocialPick = ['facebook', 'instagram', 'tiktok'].some((p) => step3.has(p));
  if (hasSocialPick) {
    patch.socialMedia = mergeJoySocialEnables(current, clean.step3Modules);
  }

  const sec = clean.editableSection;
  const title = String(sec.title || '').trim();
  const text = String(sec.text || '').trim();
  const imageUrl = String(sec.imageUrl || '').trim();
  const extraPromo = (Array.isArray(options.extraPromoImages) ? options.extraPromoImages : [])
    .filter((u) => typeof u === 'string' && u.startsWith('https://'));
  if (title || text || imageUrl || extraPromo.length) {
    const existingPromo = current.clientPromoGallery || { enabled: false, title: '', images: [] };
    const baseImages = imageUrl
      ? [imageUrl]
      : Array.isArray(existingPromo.images)
        ? existingPromo.images
        : [];
    const promoImages = [...baseImages, ...extraPromo.filter((u) => !baseImages.includes(u))].slice(0, 10);
    patch.clientPromoGallery = {
      enabled: true,
      title: title || existingPromo.title || 'Ofertele noastre',
      subtitle: text || existingPromo.subtitle || '',
      images: promoImages
    };
  }

  if (clean.growthModule === 'loyalty') {
    patch.loyaltySettings = { ...(current.loyaltySettings || {}), active: true };
  } else if (clean.growthModule === 'events') {
    patch.eventModule = { ...(current.eventModule || {}), enabled: true };
  }

  if (!Object.keys(patch).length) return current;

  await saveCampaignSettingsBackend(patch);

  const merged = { ...current, ...patch };
  if (patch.socialMedia) merged.socialMedia = patch.socialMedia;
  if (patch.clientPromoGallery) merged.clientPromoGallery = patch.clientPromoGallery;
  if (patch.loyaltySettings) merged.loyaltySettings = patch.loyaltySettings;
  if (patch.eventModule) merged.eventModule = patch.eventModule;
  setCurrentSettings(merged);
  return merged;
}

export function buildJoyPreviewSettings(settings = {}, joyState = {}) {
  if (!settings || !joyState) return settings;
  const merged = { ...settings };
  const images = (joyState.images || []).filter(
    (u) => typeof u === 'string' && (u.startsWith('http') || u.startsWith('blob:'))
  );
  if (images.length) {
    merged.bgURLs = [...images];
    merged.bgURL = images[0];
  }
  if (joyState.slideshowSeconds) merged.slideDuration = joyState.slideshowSeconds;
  merged.socialMedia = mergeJoySocialEnables(settings, joyState.step3Modules || []);
  return merged;
}

export { MAX_IMAGES, TOTAL_STEPS, FLOW_VERSION };
